import React, { useCallback, useMemo } from 'react'
import dayjs from 'dayjs'
import { useTranslation } from 'react-i18next'
import {
  Avatar,
  Box,
  Button,
  Callout,
  DataTable,
  DataTableCellVariants,
  DataTableHeaderProps,
  DataTableRowData,
  EmptyState,
  RowsSkeletonLoader,
  useSidePanel,
} from '@node-space/ui-components'
import { Lottie } from 'components/Animations/SimpleLottie'
import { ExternalLink } from 'components/ExternalLink/ExternalLink'
import { DateFormat } from 'constants/General'
import { MIN_PAGINATION_SIZE } from 'constants/queries'
import { useApprovalsContext } from 'hooks/context/useApprovalsContext'
import { ApprovalTableTypes } from 'pages/Approvals/types'
import { getTableHeaders } from 'pages/Approvals/util'
import { ApprovalStatuses } from 'types/approvals'
import { SidePanelSummary } from '../SidePanel/SidePanelSummary'

export const PendingTransactions = () => {
  const { t } = useTranslation()

  const {
    pendingApprovalDetails: { pendingApprovals, hasPendingApprovals },
    permissions,
    isFetching,
    isError,
    approvalsConfig: {
      allApprovalFlowsAreDisabled,
      handleTableRowCheck,
      handleManageApproversModal,
      handleApprovalPreferenceSettingsModal,
    },
  } = useApprovalsContext()

  const {
    isSidePanelOpen: isPendingApprovalDetailsOpen,
    sidePanelData: pendingApprovalDetails,
    paging,
    setIsSidePanelOpen,
    updateSidePanel,
  } = useSidePanel(pendingApprovals)

  const openSidePanelSummary = (index: number) => {
    updateSidePanel(index)
    setIsSidePanelOpen(true)
  }

  const columns: DataTableHeaderProps[] = useMemo(() => {
    return getTableHeaders(t, ApprovalTableTypes.PENDING)
  }, [t])

  const tableData: DataTableRowData[] = useMemo(() => {
    if (!pendingApprovals) return []

    return pendingApprovals?.map((approvalTransaction, index) => {
      return {
        id: approvalTransaction?.correlationKey,
        testid: approvalTransaction?.correlationKey,
        onRowClick: () => {
          openSidePanelSummary(index)
        },
        cells: [
          {
            key: 'payment',
            variant: DataTableCellVariants.TEXT,
            Avatar: <Avatar size="small" name={approvalTransaction?.currency?.code} />,
            Text: `${approvalTransaction?.amount} ${approvalTransaction?.currency?.code}`,
            BottomText: approvalTransaction?.walletName,
          },
          {
            key: 'to',
            variant: DataTableCellVariants.TEXT,
            Text:
              approvalTransaction?.beneficiaryDetails?.accountNumber ||
              approvalTransaction?.beneficiaryDetails?.destinationWallet,
            BottomText: approvalTransaction?.beneficiaryDetails?.beneficiaryName,
          },
          {
            key: 'approvalsInitiatedBy',
            variant: DataTableCellVariants.TEXT,
            Text: approvalTransaction?.initiatedBy || approvalTransaction?.initiatedByEmail,
            BottomText: dayjs(approvalTransaction?.actionedAt).format(
              DateFormat.D_MMM_YYYY_AT_HH_MM
            ),
          },
          {
            key: 'approvalsRequired',
            variant: DataTableCellVariants.TEXT,
            Text: !!approvalTransaction?.approvalsCounter
              ? `${approvalTransaction?.approvalsCounter?.approvalsRequired - approvalTransaction?.approvalsCounter?.approvalsAcquired}`
              : '-',
          },
        ],
      }
    })
  }, [pendingApprovals])

  const handleTableRowSelection = useCallback(
    (selectedRows: DataTableRowData[]) => {
      const selectedTableData = selectedRows.reduce((tableRows, current) => {
        const selectedTx = pendingApprovals?.find(tx => tx?.correlationKey === current?.id)

        return [...tableRows, ...(selectedTx ? [selectedTx] : [])]
      }, [])

      handleTableRowCheck(selectedTableData)
    },
    [pendingApprovals]
  )

  return (
    <Box paddingB={16}>
      {allApprovalFlowsAreDisabled && hasPendingApprovals && !isFetching && (
        <Box paddingB={16}>
          <Callout variant="warning">{t('approvals.disableApprovalCalloutDisclaimer')}</Callout>
        </Box>
      )}

      {isFetching && <RowsSkeletonLoader rows={MIN_PAGINATION_SIZE} />}

      {!isFetching && isError && (
        <EmptyState
          testId="error-state"
          LottiePlayer={<Lottie src="connection-error" />}
          title={t('oopsSomethingWentWrong')}
        />
      )}

      {!isFetching && !isError && !hasPendingApprovals && (
        <EmptyState
          testId="empty-state"
          LottiePlayer={<Lottie src="no-data" />}
          title={
            allApprovalFlowsAreDisabled
              ? t('approvals.disabledApprovalsHeadline')
              : t('approvals.enabledAndNoPendingApprovalsHeadline')
          }
          description={
            <>
              {allApprovalFlowsAreDisabled
                ? t('approvals.disabledApprovalsDescription')
                : t('approvals.enabledAndNoPendingApprovalsDescription')}{' '}
              <ExternalLink href={`${process.env.ABOUT_APPROVAL_LINK}`}>
                {t('learnMore')}
              </ExternalLink>
            </>
          }
          PrimaryButton={
            <Button
              disabled={!permissions?.canEdit}
              onClick={() => handleApprovalPreferenceSettingsModal({ isVisible: true })}
            >
              {t('approvals.enableAndEditPreferences')}
            </Button>
          }
          SecondaryButton={
            !allApprovalFlowsAreDisabled &&
            !hasPendingApprovals && (
              <Button
                variant="secondary"
                disabled={!permissions?.canEdit}
                onClick={() => handleManageApproversModal({ isVisible: true })}
              >
                {t('approvals.manageApprovers')}
              </Button>
            )
          }
        />
      )}

      {!isFetching && !isError && hasPendingApprovals && (
        <>
          <DataTable
            name="pending-transactions-table"
            headers={columns}
            data={tableData}
            isSelectable={true}
            onTableRowSelection={handleTableRowSelection}
          />
          {pendingApprovalDetails && (
            <SidePanelSummary
              isOpen={isPendingApprovalDetailsOpen}
              paging={paging}
              onClose={() => setIsSidePanelOpen(false)}
              transactionDetails={pendingApprovalDetails}
              tab={ApprovalStatuses.PENDING}
            />
          )}
        </>
      )}
    </Box>
  )
}
